import { mainEvent, race, registrationPhase } from '../../shared/race.config'
import { formatClock, formatDate, formatLongDate, formatMoney } from '../../shared/format'
import { course, feet } from '../lib/course'

export type FaqItem = {
  q: string
  a: string
  topic: 'Registration' | 'Giving' | 'Race day' | 'Course' | 'Policies'
  /** Shown on the home page. */
  featured?: boolean
}

const m = race.memorial
// PLACEHOLDER: name the charities here once they're confirmed (and drop "named at a later date" below)
const charities = 'charities benefiting suicide prevention and mental health'

/** Draft answers for the race director to confirm. */
export const faq: FaqItem[] = [
  {
    topic: 'Registration',
    q: 'How much does it cost?',
    a:
      registrationPhase() === 'upcoming'
        ? 'We’ll share pricing when registration opens. Every registration includes a purple race shirt and a finisher medal. Leave your email on the Register page and we’ll let you know the moment it opens.'
        : `Registration is ${formatMoney(mainEvent.tiers[0].cents)}, including a purple race shirt and a finisher medal. A small card processing fee is added at checkout so the race keeps the full entry fee.`,
  },
  {
    topic: 'Registration',
    q: 'Can I register my whole family or group in one go?',
    a: 'Yes. Add up to 10 people in one checkout, each with their own shirt size.',
    featured: true,
  },
  {
    topic: 'Registration',
    q: 'When does registration open?',
    a:
      registrationPhase() === 'upcoming'
        ? `Registration isn’t open yet. Leave your email on the Register page and we’ll email you when it opens. Online registration then runs until ${formatDate(race.registrationClosesAt)}, or until the race sells out (the field is capped at ${race.capacity}).`
        : `Online registration closes ${formatDate(race.registrationClosesAt)}, or earlier if the race sells out (the field is capped at ${race.capacity}). If spots remain, you can sign up at the race-day tent.`,
    featured: true,
  },
  {
    topic: 'Registration',
    q: 'Is my shirt size guaranteed?',
    a: `Sizes are guaranteed for everyone registered by ${formatDate(race.shirtGuaranteeAt)}. After that we’ll do our best, but sizes may run out.`,
  },
  {
    topic: 'Registration',
    q: 'Is my payment secure?',
    a: 'Payments go through Stripe. Your card details are entered on Stripe’s secure page and never reach our servers. Apple Pay and Google Pay work too.',
  },
  {
    topic: 'Giving',
    q: 'Where does the money go?',
    a: `Proceeds from this independently organized 5K will be donated to ${charities} following the event, after event expenses. The charities will be named at a later date. Sponsors cover event costs wherever they can, so more goes to the cause. Registration fees are not tax-deductible charitable contributions.`,
    featured: true,
  },
  {
    topic: 'Giving',
    q: 'Can I donate without running?',
    a: `Yes. Use the Donate page to give any amount in ${m.name}’s memory, or add a donation when you register. Donations made through this site are collected by the event organizers and will be contributed to ${charities} following the event. They are not contributions made directly to those charities and may not be tax-deductible.`,
    featured: true,
  },
  {
    topic: 'Giving',
    q: 'Why purple?',
    a: `Purple was ${m.name}’s favorite color. The race shirts are purple, and we’d love everyone to wear some on race day.`,
  },
  {
    topic: 'Race day',
    q: 'Where do I pick up my bib?',
    a: `Packet pickup is on race morning only, from ${formatClock(race.schedule[0].time)}, in the event area at Groves 5–11 (Busse Forest South). Bring your confirmation code or photo ID. You can pick up for a friend with their confirmation code.`,
  },
  {
    topic: 'Race day',
    q: 'Where do I park?',
    a: `Parking is free in the Busse Forest South lots along the park drive by Groves 5–11 (enter from Higgins Road, just west of Arlington Heights Road). Lots fill up, so please carpool and aim to arrive by ${formatClock('08:30')}.`,
    featured: true,
  },
  {
    topic: 'Race day',
    q: 'Are there restrooms?',
    a: 'Yes. There are restrooms near the start area and we add portable restrooms for race morning.',
  },
  {
    topic: 'Race day',
    q: 'Is the race chip timed? When are results posted?',
    a: 'Yes, with a tag on the back of your bib. Results are posted on the results page on race day.',
  },
  {
    topic: 'Course',
    q: 'Is the course stroller and wheelchair friendly?',
    a: `Yes. The whole route is paved and mostly flat (about ${feet(course.elevation.gainM)} feet of total climb, including the ramp up to the Higgins Road trail bridge). Strollers start at the back of the field. Wheelchair athletes are welcome: email us so we can plan your start.`,
  },
  {
    topic: 'Course',
    q: 'Is there a time limit?',
    a: 'You have 90 minutes, which is a relaxed walking pace of 18 minutes per kilometer (29 minutes per mile). A course sweep walks in with the final finisher.',
  },
  {
    topic: 'Course',
    q: 'Can I wear headphones? Can I bring my dog?',
    a: 'The trail stays open to other Busse Woods visitors, so keep the volume low and one ear free, and keep right on the out-and-back. Please leave dogs with a supporter during the race: leashed dogs are welcome to cheer from the start area.',
  },
  {
    topic: 'Policies',
    q: 'Can I get a refund or transfer my entry?',
    a: `Refunds (minus the processing fee) are available until ${formatDate(race.refundDeadlineAt)}. You can transfer your entry to another runner for free until the same date: just email us with their details.`,
  },
  {
    topic: 'Policies',
    q: 'What happens if the weather is bad?',
    a: `The race goes ahead in rain or heat. July mornings can be hot, so there’s extra water and ice on course and at the finish. If lightning or extreme heat makes it unsafe, we may delay or cancel the start. Shirts and medals are still handed out or mailed if the race can’t go ahead on ${formatLongDate(race.startsAt)}.`,
  },
  {
    topic: 'Policies',
    q: 'Can runners under 18 take part?',
    a: `Yes, from age ${mainEvent.minAge}. A parent or guardian signs the waiver during registration for anyone under 18 on race day.`,
  },
]
